import { useEffect, useMemo, useState } from "react";
import { HyperGraphFlow } from "../components/graph/HyperGraphFlow";
import { WhyPanel } from "../components/why/WhyPanel";
import { NODE_PALETTE, type NodeKind } from "../types/ontology";
import { ExpandableGraphFrame } from "../components/integrations/ExpandableGraphFrame";
import type { Graph3DNode, Graph3DLink } from "../components/integrations/Graph3D";
import { useActiveProjectGraph } from "../lib/useActiveProjectGraph";
import { mergeProjectLayers } from "../lib/projectLayers";

export default function Graph(): JSX.Element {
  const [focusedNodeId, setFocusedNodeId] = useState<string>("al1");
  const [filterKind, setFilterKind] = useState<NodeKind | "ALL">("ALL");
  // Re-merge on timeline + project-change events so newly-emitted
  // CatalogImport activities appear in the project map immediately.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1);
    window.addEventListener("foyrmind:timeline", bump);
    window.addEventListener("foyrmind:projectChanged", bump);
    return () => {
      window.removeEventListener("foyrmind:timeline", bump);
      window.removeEventListener("foyrmind:projectChanged", bump);
    };
  }, []);
  const { graph: baseGraph, activeId } = useActiveProjectGraph();
  // Merge in CatalogImports + Synthesis + user placements so the
  // project map shows every typed mutation, not just the base graph.
  const projectGraph = useMemo(
    () => { void tick; return mergeProjectLayers(baseGraph, activeId); },
    [baseGraph, activeId, tick],
  );

  const highlight = useMemo(() => {
    if (filterKind === "ALL") return new Set<string>();
    const ids = projectGraph.nodes.filter((n) => n.kind === filterKind).map((n) => n.id);
    return new Set(ids);
  }, [filterKind, projectGraph.nodes]);

  const counts = useMemo(() => {
    const m: Partial<Record<NodeKind, number>> = {};
    for (const n of projectGraph.nodes) {
      m[n.kind] = (m[n.kind] ?? 0) + 1;
    }
    return m;
  }, [projectGraph.nodes]);

  const kinds: (NodeKind | "ALL")[] = ["ALL", ...((Object.keys(NODE_PALETTE)) as NodeKind[])];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 sm:py-14">
      <div className="mb-8">
        <div className="eyebrow">Hypergraph explorer</div>
        <h1 className="display-2 mt-2 text-ink-950">
          {projectGraph.projectName}
        </h1>
        <p className="mt-3 text-ink-700 max-w-3xl">
          {projectGraph.nodes.length} typed nodes · {projectGraph.edges.length} edges ·
          built from the real-world schemas in <a className="underline underline-offset-2" href="/#data">Data sources</a>.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-6">
        <div className="space-y-3">
          <div className="panel p-3">
            <div className="flex flex-wrap gap-1.5">
              {kinds.map((k) => {
                const palette = k === "ALL" ? null : NODE_PALETTE[k];
                const count = k === "ALL" ? projectGraph.nodes.length : counts[k] ?? 0;
                return (
                  <button
                    key={k}
                    onClick={() => setFilterKind(k)}
                    className={`pill border transition-all ${
                      filterKind === k
                        ? "bg-ink-950 text-cream border-ink-950"
                        : "bg-cream border-border text-ink-700 hover:border-foyr"
                    }`}
                  >
                    {palette ? (
                      <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: palette.hex }} />
                    ) : null}
                    {k === "ALL" ? "All" : palette?.label} <span className="opacity-60 ml-1">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {(() => {
            const g = projectGraph;
            const graph3d = {
              nodes: g.nodes.map<Graph3DNode>((n) => ({
                id: n.id, label: n.label, kind: n.kind.toLowerCase(),
                category: String(
                  n.attrs["category"] ?? n.attrs["roomKind"] ?? n.attrs["zoneKind"] ?? "",
                ),
              })),
              links: g.edges.map<Graph3DLink>((e) => ({
                source: e.source, target: e.target, label: e.kind,
                emphasis: /RULE_FIRED|BLOCKED_BY|DEPENDS_ON|RULE_RECOMMENDS/.test(e.kind) ? "primary" : "neutral",
              })),
            };
            return (
              <ExpandableGraphFrame
                title={`Project map · ${g.projectName}`}
                subtitle={`${g.nodes.length} typed nodes · ${g.edges.length} edges · expand for 2D fullscreen + 3D WebGL`}
                graph3d={graph3d}
              >
                <HyperGraphFlow
                  highlightNodeIds={highlight}
                  onNodeClick={(n) => setFocusedNodeId(n.id)}
                  height={680}
                  direction="LR"
                />
              </ExpandableGraphFrame>
            );
          })()}
        </div>
        <WhyPanel
          focusedNodeId={focusedNodeId}
          onFocusChange={setFocusedNodeId}
          pickableNodeKinds={["Alert", "Approval", "Milestone", "SKU", "BudgetLine", "Rule", "MemoryCard"]}
        />
      </div>
    </div>
  );
}
